import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import { Typography } from "@/components/ui/typography"
import {
  SettingsField,
  SettingsSection,
} from "@/features/settings/components/SettingsPage"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import type { BillingPlanId } from "@/services/api/billingService"
import { URLS } from "@/utils/urls.const"
import {
  BillingAddressElement,
  PaymentElement,
  useCheckout,
  type StripeCheckoutElementsValue,
} from "@stripe/react-stripe-js/checkout"
import type {
  StripeAddressElementChangeEvent,
  StripeCheckoutTaxIdType,
} from "@stripe/stripe-js"
import { useEffect, useRef, useState } from "react"
import { CheckoutLayout } from "./CheckoutLayout"
import { OrderSummary } from "./OrderSummary"

const EU_VAT_COUNTRIES = new Set(
  "AT BE BG CY CZ DE DK EE ES FI FR GR HR HU IE IT LT LU LV MT NL PL PT RO SE SI SK".split(
    " ",
  ),
)
const OTHER_VAT_TYPES: Record<string, StripeCheckoutTaxIdType> = {
  CH: "ch_vat",
  GB: "gb_vat",
  IS: "is_vat",
  LI: "li_vat",
  NO: "no_vat",
}

/**
 * EU companies outside Germany: Stripe Tax only reverse charges with their VAT
 * ID; without one it charges no VAT at all (we have no OSS registration).
 */
const taxIdRequired = (country: string | undefined) =>
  country !== undefined && country !== "DE" && EU_VAT_COUNTRIES.has(country)

/** EU VAT IDs start with their country's code; Greece's is EL. */
const vatPrefix = (country: string) => (country === "GR" ? "EL" : country)

/** Stripe's tax ID type for a VAT number in the billing country; null where we take none. */
const taxIdType = (
  country: string | undefined,
  value: string,
): StripeCheckoutTaxIdType | null => {
  // Northern Ireland's XI numbers are EU VAT numbers behind a GB address
  if (value.startsWith("XI")) return "eu_vat"
  if (!country) {
    if (value.startsWith("CHE")) return "ch_vat"
    return value.startsWith("GB") ? "gb_vat" : "eu_vat"
  }
  if (EU_VAT_COUNTRIES.has(country)) return "eu_vat"
  return OTHER_VAT_TYPES[country] ?? null
}

/**
 * What the buyer entered in the billing address element. Stripe keeps it off
 * the session until the payment (checkout.billingAddress stays null).
 */
type BillingAddress = StripeAddressElementChangeEvent["value"]

type TaxIdOutcome =
  | { type: "unchanged" }
  | { type: "refused"; message: string }
  /** The total due today after Stripe Tax ran again (reverse charge on or off). */
  | { type: "updated"; total: number }

/**
 * Sends the VAT ID the user typed to the session; null means leave Stripe's
 * value alone. Refuses to go on without one where it is required.
 */
const applyTaxId = async (
  checkout: StripeCheckoutElementsValue,
  raw: string | null,
  billing: BillingAddress | null,
  messages: { unsupported: string; required: string; otherCountry: string },
): Promise<TaxIdOutcome> => {
  const country = billing?.address.country
  // The ID the payment goes out with: the typed one, else the one Stripe holds.
  // Not one saved on a returning customer: Stripe applies that unseen
  // (taxIdInfo stays null), whatever its country, so they type it again.
  const held = (raw ?? checkout.taxIdInfo?.taxId.value ?? "")
    .toUpperCase()
    .replace(/\s+/g, "")
  if (!held && taxIdRequired(country)) {
    return { type: "refused", message: messages.required }
  }
  // Stripe reverse charges on the format alone, also for another country's ID
  if (
    held &&
    country &&
    EU_VAT_COUNTRIES.has(country) &&
    !held.startsWith(vatPrefix(country))
  ) {
    return { type: "refused", message: messages.otherCountry }
  }
  if (raw === null) return { type: "unchanged" }
  const value = raw.trim().toUpperCase()
  const compact = value.replace(/\s+/g, "")
  if (!compact && !checkout.taxIdInfo) return { type: "unchanged" }
  const type = taxIdType(country, compact)
  if (compact && !type)
    return { type: "refused", message: messages.unsupported }
  const result = await checkout.updateTaxIdInfo(
    compact && type
      ? {
          // Swiss numbers keep their format ("CHE-123.456.789 MWST"); EU and UK ones go compact
          taxId: { type, value: type === "ch_vat" ? value : compact },
          // The buyer's company from the billing address (checkout.businessName is our own, the merchant's)
          businessName: billing?.name || checkout.taxIdInfo?.businessName || "",
        }
      : null,
  )
  return result.type === "error"
    ? { type: "refused", message: result.error.message }
    : { type: "updated", total: result.session.total.total.minorUnitsAmount }
}

interface CheckoutFormProps {
  plan: BillingPlanId
  onBack: () => void
  onPaid: () => void
  onError: () => void
  /** True while a payment or a promotion code change runs, so the page can hold its navigation. */
  onPayingChange: (paying: boolean) => void
}

/**
 * Billing and payment as settings sections, the order summary with the pay
 * button beside them. Stripe validates its fields on confirm; a payment that
 * needs a redirect comes back through /upgrade/return.
 */
export const CheckoutForm: React.FC<CheckoutFormProps> = ({
  plan,
  onBack,
  onPaid,
  onError,
  onPayingChange,
}) => {
  const { translate } = useTranslation()
  const state = useCheckout()
  // null until the user types: an ID Stripe already holds is then left as it is
  const [taxId, setTaxId] = useState<string | null>(null)
  const [taxIdError, setTaxIdError] = useState<string | null>(null)
  const taxIdInput = useRef<HTMLInputElement>(null)
  const [address, setAddress] = useState<BillingAddress | null>(null)
  const [pending, setPending] = useState(false)
  const [promoPending, setPromoPending] = useState(false)
  // Back to the plans and in again reopens this session, past any hold on the pay button
  const busy = pending || promoPending
  const [error, setError] = useState<string | null>(null)
  const failed = state.type === "error"
  useEffect(() => {
    if (failed) onError()
  }, [failed, onError])
  useEffect(() => {
    onPayingChange(busy)
  }, [busy, onPayingChange])

  if (state.type !== "success") {
    return (
      <CheckoutLayout
        onBack={onBack}
        summary={<Skeleton className="h-96 w-full rounded-xl" />}
      >
        <div className="flex flex-col gap-4" aria-busy="true">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      </CheckoutLayout>
    )
  }
  const { checkout } = state
  const required = taxIdRequired(address?.address.country)

  const submit = async () => {
    // What the summary shows as the user clicks
    const shownTotal = checkout.total.total.minorUnitsAmount
    setPending(true)
    setError(null)
    let paid = false
    try {
      // The element's own value, not the last change event: the address Stripe bills
      const billing =
        (await checkout.getBillingAddressElement()?.getValue())?.value ??
        address
      setAddress(billing)
      const country = billing?.address.country
      const vat = await applyTaxId(checkout, taxId, billing, {
        unsupported: translate("UPGRADE/CHECKOUT_TAX_ID_UNSUPPORTED"),
        required: translate("UPGRADE/CHECKOUT_TAX_ID_REQUIRED"),
        otherCountry: translate("UPGRADE/CHECKOUT_TAX_ID_OTHER_COUNTRY", {
          prefix: country ? vatPrefix(country) : "",
        }),
      })
      setTaxIdError(vat.type === "refused" ? vat.message : null)
      if (vat.type === "refused") {
        // The field sits apart from the pay button: take the user to it
        taxIdInput.current?.focus()
        return
      }
      if (vat.type === "updated" && vat.total !== shownTotal) {
        // Never charge a total the user has not seen: the summary now shows the new one
        setError(translate("UPGRADE/CHECKOUT_TOTAL_CHANGED"))
        return
      }
      const result = await checkout.confirm({ redirect: "if_required" })
      if (result.type === "success") {
        paid = true
        onPaid()
        return
      }
      setError(result.error.message || translate("UPGRADE/CHECKOUT_FAILED"))
    } catch {
      // A thrown call (network, Stripe.js) must not leave the button spinning
      setError(translate("UPGRADE/CHECKOUT_FAILED"))
    } finally {
      if (!paid) setPending(false)
    }
  }

  return (
    <CheckoutLayout
      onBack={onBack}
      backDisabled={busy}
      summary={
        <OrderSummary
          plan={plan}
          checkout={checkout}
          pending={pending}
          promoPending={promoPending}
          onPromoPendingChange={setPromoPending}
          error={error}
          onSubmit={() => void submit()}
        />
      }
    >
      <div className="flex flex-col divide-y">
        <SettingsSection
          title={translate("UPGRADE/CHECKOUT_BILLING_ADDRESS")}
          description={translate("UPGRADE/CHECKOUT_BILLING_ADDRESS_HINT")}
        >
          <BillingAddressElement
            options={{ display: { name: "organization" } }}
            onChange={(event) => setAddress(event.value)}
          />
          <SettingsField
            htmlFor="checkout-tax-id"
            label={translate(
              required
                ? "UPGRADE/CHECKOUT_TAX_ID"
                : "UPGRADE/CHECKOUT_TAX_ID_OPTIONAL",
            )}
          >
            <Input
              ref={taxIdInput}
              id="checkout-tax-id"
              value={taxId ?? checkout.taxIdInfo?.taxId.value ?? ""}
              onChange={(event) => setTaxId(event.target.value)}
              aria-required={required || undefined}
              aria-invalid={taxIdError ? true : undefined}
              // "e.g.": a bare example number reads as one already filled in
              placeholder={translate("UPGRADE/CHECKOUT_TAX_ID_PLACEHOLDER")}
              autoComplete="off"
            />
            {taxIdError && (
              <Typography as="small" className="text-destructive" role="alert">
                {taxIdError}
              </Typography>
            )}
            {required && (
              // A buyer without one (an association, a small business) cannot pay here
              <Typography as="small" className="text-muted-foreground">
                {translate("UPGRADE/CHECKOUT_TAX_ID_NONE")}{" "}
                <a href={URLS.INFO_BOX.HELP} className="underline">
                  {translate("TEAM/CONTACT_US")}
                </a>
              </Typography>
            )}
          </SettingsField>
        </SettingsSection>
        <SettingsSection title={translate("UPGRADE/CHECKOUT_PAYMENT_METHOD")}>
          <PaymentElement options={{ layout: "accordion" }} />
        </SettingsSection>
      </div>
    </CheckoutLayout>
  )
}
